<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  webhook: {
    type: Object,
    default: null,
  },
  pipelines: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close', 'saved']);
const store = useStore();
const { showAlert } = useAlert();

const { t } = useI18n();

const isEditing = computed(() => !!props.webhook?.id);
const isSaving = ref(false);
const showSecret = ref(false);

const EVENT_CONFIG = [
  {
    value: 'pipeline_card_created',
    icon: 'i-lucide-plus-circle',
    color: 'text-success',
  },
  {
    value: 'pipeline_card_updated',
    icon: 'i-lucide-pencil-line',
    color: 'text-n-blue-11',
  },
  {
    value: 'pipeline_card_deleted',
    icon: 'i-lucide-trash-2',
    color: 'text-danger',
  },
  {
    value: 'pipeline_card_stage_changed',
    icon: 'i-lucide-arrow-right-circle',
    color: 'text-n-yellow-11',
  },
  {
    value: 'pipeline_card_won',
    icon: 'i-lucide-trophy',
    color: 'text-success',
  },
  {
    value: 'pipeline_card_lost',
    icon: 'i-lucide-circle-x',
    color: 'text-danger',
  },
  {
    value: 'pipeline_card_owner_changed',
    icon: 'i-lucide-user-round-cog',
    color: 'text-n-purple-11',
  },
];

const availableEvents = computed(() =>
  EVENT_CONFIG.map(e => ({
    ...e,
    label: t(`PIPELINE_WEBHOOK.FORM.EVENTS.${e.value}.LABEL`),
    description: t(`PIPELINE_WEBHOOK.FORM.EVENTS.${e.value}.DESCRIPTION`),
  }))
);

const form = ref({
  name: '',
  url: '',
  pipeline_id: null,
  events: [],
  active: true,
});

watch(
  () => props.webhook,
  webhook => {
    if (webhook) {
      form.value = {
        name: webhook.name || '',
        url: webhook.url || '',
        pipeline_id: webhook.pipeline_id || null,
        events: [...(webhook.events || [])],
        active: webhook.active !== false,
      };
    } else {
      form.value = {
        name: '',
        url: '',
        pipeline_id: null,
        events: [],
        active: true,
      };
    }
  },
  { immediate: true }
);

const URL_REGEX = /^https?:\/\/[^\s]+\..+$/;

const isUrlValid = computed(
  () => !form.value.url.trim() || URL_REGEX.test(form.value.url.trim())
);

const isFormValid = computed(() => {
  return (
    form.value.name.trim() &&
    form.value.url.trim() &&
    URL_REGEX.test(form.value.url.trim()) &&
    form.value.events.length > 0
  );
});

const allEventsSelected = computed(
  () => form.value.events.length === availableEvents.value.length
);

const toggleAllEvents = () => {
  if (allEventsSelected.value) {
    form.value.events = [];
  } else {
    form.value.events = availableEvents.value.map(e => e.value);
  }
};

const handleSave = async () => {
  if (!isFormValid.value) return;
  isSaving.value = true;
  try {
    const payload = { pipeline_webhook: { ...form.value } };
    if (isEditing.value) {
      await store.dispatch('pipelineWebhooks/update', {
        id: props.webhook.id,
        ...payload,
      });
    } else {
      await store.dispatch('pipelineWebhooks/create', payload);
    }
    emit('saved');
    showAlert(t('PIPELINE_WEBHOOK.MESSAGES.SAVE_SUCCESS'));
  } catch {
    showAlert(t('PIPELINE_WEBHOOK.MESSAGES.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const copySecret = async () => {
  if (!props.webhook?.secret) return;
  await navigator.clipboard.writeText(props.webhook.secret);
  showAlert(t('PIPELINE_WEBHOOK.MESSAGES.SECRET_COPIED'));
};

const handleRegenerateSecret = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('PIPELINE_WEBHOOK.MESSAGES.REGENERATE_CONFIRM')))
    return;
  try {
    await store.dispatch('pipelineWebhooks/regenerateSecret', props.webhook.id);
    showAlert(t('PIPELINE_WEBHOOK.MESSAGES.REGENERATE_SUCCESS'));
  } catch {
    showAlert(t('PIPELINE_WEBHOOK.MESSAGES.REGENERATE_ERROR'));
  }
};
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-[2px]"
    @click.self="$emit('close')"
  >
    <div
      class="bg-n-background rounded-t-2xl sm:rounded-xl shadow-2xl w-full max-w-lg sm:mx-4 max-h-[92vh] flex flex-col"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-5 pt-5 pb-4 border-b border-n-weak"
      >
        <div class="flex items-center gap-3">
          <div
            class="w-8 h-8 rounded-lg bg-n-brand/10 flex items-center justify-center shrink-0"
          >
            <Icon icon="i-lucide-webhook" class="size-4 text-n-brand" />
          </div>
          <div>
            <h3 class="text-sm font-semibold text-n-slate-12 leading-none">
              {{
                isEditing
                  ? $t('PIPELINE_WEBHOOK.MODAL.EDIT_TITLE')
                  : $t('PIPELINE_WEBHOOK.MODAL.CREATE_TITLE')
              }}
            </h3>
            <p class="text-xs text-n-slate-11 mt-0.5">
              {{
                isEditing
                  ? $t('PIPELINE_WEBHOOK.DESCRIPTION')
                  : $t('PIPELINE_WEBHOOK.DESCRIPTION')
              }}
            </p>
          </div>
        </div>
        <button
          class="p-1.5 rounded-lg text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
          @click="$emit('close')"
        >
          <Icon icon="i-lucide-x" class="size-4" />
        </button>
      </div>

      <!-- Form -->
      <div class="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-5">
        <!-- Name -->
        <div>
          <label
            class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5"
          >
            {{ $t('PIPELINE_WEBHOOK.FORM.NAME_LABEL') }}
          </label>
          <div
            class="flex items-center rounded-lg border border-n-weak bg-n-background focus-within:ring-2 focus-within:ring-n-brand/30 focus-within:border-n-brand transition-colors"
          >
            <Icon
              icon="i-lucide-tag"
              class="ml-3 size-3.5 text-n-slate-10 shrink-0 pointer-events-none"
            />
            <input
              v-model="form.name"
              type="text"
              class="reset-base flex-1 pl-2 pr-3 py-2 bg-transparent text-sm text-n-slate-12 placeholder:text-n-slate-9 border-none outline-none"
              :placeholder="$t('PIPELINE_WEBHOOK.FORM.NAME_PLACEHOLDER')"
            />
          </div>
        </div>

        <!-- URL -->
        <div>
          <label
            class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5"
          >
            {{ $t('PIPELINE_WEBHOOK.FORM.URL_LABEL') }}
          </label>
          <div
            class="flex items-center rounded-lg border border-n-weak bg-n-background focus-within:ring-2 focus-within:ring-n-brand/30 focus-within:border-n-brand transition-colors"
          >
            <Icon
              icon="i-lucide-link"
              class="ml-3 size-3.5 text-n-slate-10 shrink-0 pointer-events-none"
            />
            <input
              v-model="form.url"
              type="url"
              class="reset-base flex-1 pl-2 pr-3 py-2 bg-transparent text-sm text-n-slate-12 font-mono placeholder:text-n-slate-9 placeholder:font-sans border-none outline-none"
              :placeholder="$t('PIPELINE_WEBHOOK.FORM.URL_PLACEHOLDER')"
            />
          </div>
          <p
            v-if="form.url.trim() && !isUrlValid"
            class="mt-1 text-xs text-n-ruby-11"
          >
            {{ $t('PIPELINE_WEBHOOK.FORM.URL_INVALID') }}
          </p>
        </div>

        <!-- Pipeline Scope -->
        <div>
          <label
            class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5"
          >
            {{ $t('PIPELINE_WEBHOOK.FORM.PIPELINE_LABEL') }}
          </label>
          <div
            class="flex items-center rounded-lg border border-n-weak bg-n-background focus-within:ring-2 focus-within:ring-n-brand/30 focus-within:border-n-brand transition-colors relative"
          >
            <Icon
              icon="i-lucide-globe"
              class="ml-3 size-3.5 text-n-slate-10 shrink-0 pointer-events-none"
            />
            <select
              v-model="form.pipeline_id"
              class="no-margin reset-base flex-1 !m-0 !h-auto pl-2 pr-8 py-2 bg-transparent bg-none text-sm text-n-slate-12 border-none outline-none appearance-none cursor-pointer"
            >
              <option :value="null">
                {{ $t('PIPELINE_WEBHOOK.FORM.ALL_PIPELINES') }}
              </option>
              <option v-for="p in pipelines" :key="p.id" :value="p.id">
                {{ p.name }}
              </option>
            </select>
            <Icon
              icon="i-lucide-chevron-down"
              class="absolute right-3 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-10 pointer-events-none"
            />
          </div>
          <p class="text-xs text-n-slate-10 mt-1.5">
            {{ $t('PIPELINE_WEBHOOK.FORM.PIPELINE_HINT') }}
          </p>
        </div>

        <!-- Events -->
        <div>
          <div class="flex items-center justify-between mb-2">
            <label
              class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide"
            >
              {{ $t('PIPELINE_WEBHOOK.FORM.EVENTS_LABEL') }}
              <span
                v-if="form.events.length"
                class="ml-1 px-1.5 py-px rounded-md bg-n-brand/10 text-n-brand normal-case tracking-normal font-medium"
              >
                {{ `${form.events.length}/${availableEvents.length}` }}
              </span>
            </label>
            <button
              class="text-xs text-n-blue-11 hover:text-n-blue-12 font-medium transition-colors"
              @click="toggleAllEvents"
            >
              {{
                allEventsSelected
                  ? $t('PIPELINE_WEBHOOK.FORM.DESELECT_ALL')
                  : $t('PIPELINE_WEBHOOK.FORM.SELECT_ALL')
              }}
            </button>
          </div>
          <div class="grid grid-cols-1 gap-1.5">
            <label
              v-for="event in availableEvents"
              :key="event.value"
              class="flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-all duration-100"
              :class="
                form.events.includes(event.value)
                  ? 'border-n-brand/40 bg-n-brand/5'
                  : 'border-n-weak hover:border-n-strong hover:bg-n-alpha-1'
              "
            >
              <div
                class="w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors"
                :class="
                  form.events.includes(event.value)
                    ? 'bg-n-brand border-n-brand'
                    : 'border-n-slate-8 bg-n-background'
                "
              >
                <Icon
                  v-if="form.events.includes(event.value)"
                  icon="i-lucide-check"
                  class="size-2.5 text-white"
                />
              </div>
              <input
                v-model="form.events"
                type="checkbox"
                :value="event.value"
                class="sr-only"
              />
              <Icon
                :icon="event.icon"
                class="size-4 shrink-0"
                :class="event.color"
              />
              <div class="min-w-0">
                <span
                  class="text-sm font-medium text-n-slate-12 block leading-tight"
                >
                  {{ event.label }}
                </span>
                <p class="text-xs text-n-slate-10 leading-tight mt-px">
                  {{ event.description }}
                </p>
              </div>
            </label>
          </div>
        </div>

        <!-- Active Toggle -->
        <div
          class="flex items-center justify-between py-3 px-3 rounded-lg bg-n-alpha-1 border border-n-weak"
        >
          <div class="flex items-center gap-2.5">
            <Icon
              icon="i-lucide-power"
              class="size-4 text-n-slate-11 shrink-0"
            />
            <div>
              <span
                class="text-sm font-medium text-n-slate-12 block leading-tight"
              >
                {{ $t('PIPELINE_WEBHOOK.FORM.ACTIVE_LABEL') }}
              </span>
              <p class="text-xs text-n-slate-10 mt-px">
                {{ $t('PIPELINE_WEBHOOK.FORM.ACTIVE_HINT') }}
              </p>
            </div>
          </div>
          <button
            class="relative inline-flex h-5 w-9 rounded-full transition-colors duration-200 shrink-0"
            :class="form.active ? 'bg-n-brand' : 'bg-n-slate-7'"
            @click="form.active = !form.active"
          >
            <span
              class="absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow-sm transform transition-transform duration-200"
              :class="form.active ? 'translate-x-4' : 'translate-x-0'"
            />
          </button>
        </div>

        <!-- Secret (only show when editing) -->
        <div v-if="isEditing && webhook.secret">
          <label
            class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5"
          >
            {{ $t('PIPELINE_WEBHOOK.FORM.SECRET_LABEL') }}
          </label>
          <div
            class="rounded-lg border border-n-weak bg-n-alpha-1 overflow-hidden"
          >
            <div class="flex items-center gap-0">
              <div class="flex items-center gap-2 flex-1 px-3 py-2 min-w-0">
                <Icon
                  icon="i-lucide-key-round"
                  class="size-3.5 text-n-slate-10 shrink-0"
                />
                <span
                  class="text-xs font-mono text-n-slate-11 truncate"
                  :class="showSecret ? '' : 'tracking-widest'"
                >
                  {{ showSecret ? webhook.secret : '\u2022'.repeat(24) }}
                </span>
              </div>
              <div class="flex items-center border-l border-n-weak">
                <button
                  class="p-2 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                  :title="
                    showSecret
                      ? $t('PIPELINE_WEBHOOK.FORM.HIDE')
                      : $t('PIPELINE_WEBHOOK.FORM.SHOW')
                  "
                  @click="showSecret = !showSecret"
                >
                  <Icon
                    :icon="showSecret ? 'i-lucide-eye-off' : 'i-lucide-eye'"
                    class="size-3.5"
                  />
                </button>
                <button
                  class="p-2 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                  :title="$t('PIPELINE_WEBHOOK.FORM.COPY_SECRET')"
                  @click="copySecret"
                >
                  <Icon icon="i-lucide-copy" class="size-3.5" />
                </button>
                <button
                  class="p-2 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                  :title="$t('PIPELINE_WEBHOOK.FORM.REGENERATE_SECRET')"
                  @click="handleRegenerateSecret"
                >
                  <Icon icon="i-lucide-rotate-cw" class="size-3.5" />
                </button>
              </div>
            </div>
          </div>
          <p class="text-xs text-n-slate-10 mt-1.5 flex items-center gap-1">
            <Icon icon="i-lucide-shield-check" class="size-3 shrink-0" />
            {{ $t('PIPELINE_WEBHOOK.FORM.SECRET_HINT') }}
          </p>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex items-center justify-end gap-2 px-5 py-4 border-t border-n-weak"
      >
        <Button variant="ghost" @click="$emit('close')">
          {{ $t('PIPELINE_WEBHOOK.FORM.CANCEL') }}
        </Button>
        <Button :disabled="!isFormValid || isSaving" @click="handleSave">
          <template v-if="isSaving" #icon>
            <Icon icon="i-lucide-loader-circle" class="size-4 animate-spin" />
          </template>
          {{
            isSaving
              ? $t('PIPELINE_WEBHOOK.FORM.SAVING')
              : $t('PIPELINE_WEBHOOK.FORM.SAVE')
          }}
        </Button>
      </div>
    </div>
  </div>
</template>
